/**
 * FlahaSOIL — centralized brand terminology & identity.
 *
 * Aligns with:
 *  - docs/Flaha Agri Tech Brand Identity Guidelines.md
 *  - flaha.org Digital Ecosystem Architecture (parent authority → Flaha PA → FlahaSOIL)
 *  - flaha.org/lib/seo.js product/org naming conventions
 *
 * Colour tokens stay in `flahaSoilTheme.ts`. This module is text + entity only.
 */

/** Parent organization (master brand). */
export const BRAND_ORG = "Flaha Agri Tech";

/** Division that owns the DSS products. */
export const BRAND_DIVISION = "Flaha PA";

/** Division long form. */
export const BRAND_DIVISION_FULL = "Flaha PA — Precision Agriculture";

/** Compact product wordmark. */
export const BRAND_WORDMARK = "FlahaSOIL";

/** Primary product label used on landing / auth / report surfaces. */
export const BRAND_PRODUCT_LABEL = "FlahaSOIL — Soil Intelligence Platform";

/** Product role (ecosystem architecture / flaha.org product content). */
export const BRAND_PRODUCT_ROLE = "Soil intelligence";

/** Short public-facing tagline for hero / auth surfaces. */
export const BRAND_TAGLINE =
	"Scientific soil intelligence for precision agronomy";

/**
 * Public-facing footer line. Always attributes parent brand so the app
 * remains traceable to Flaha Agri Tech (ecosystem architecture §3.1).
 */
export const BRAND_FOOTER = `© ${BRAND_WORDMARK} · ${BRAND_DIVISION} · ${BRAND_ORG}`;

/** Hierarchy line for overlines / lockups. */
export const BRAND_HIERARCHY = `${BRAND_ORG} → ${BRAND_DIVISION} → ${BRAND_WORDMARK}`;

/**
 * Short note pointing users at the corporate product page (ecosystem
 * architecture §4.3: marketing narrative on flaha.org, app on subdomain).
 */
export const BRAND_MARKETING_URL = "https://flaha.org/products/flahasoil";

/**
 * Standardized product vocabulary. Screens should reference these
 * constants rather than hand-typing near-synonyms.
 */
export const BRAND_TERMS = {
	scientificSoilReports: "Scientific Soil Reports",
	agronomicInterpretation: "Agronomic Interpretation",
	decisionReports: "Decision Reports",
	evidenceCompleteness: "Evidence Completeness",
	scientificVisualisations: "Scientific Visualisations",
	flahaCalcExport: "FlahaCalc Export",
} as const;

/** Standardized settings section labels. */
export const BRAND_SETTINGS_LABELS = {
	organization: "Organization Settings",
	account: "Preferences",
	hub: "Account",
} as const;
